import React, { useRef } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import {
  AttachFile,
  DeleteOutline,
  Download,
  Visibility
} from '@mui/icons-material';
import {
  downloadApplicationAttachment,
  downloadPendingFile,
  viewApplicationAttachment
} from '../../utils/authorizationAttachments';

/**
 * Compact per-row attachment controls for experience / training tables.
 */
function RowAttachmentsCell({
  attachments = [],
  pendingFiles = [],
  readOnly = false,
  saving = false,
  onAddFiles,
  onRemovePending,
  onDelete,
  onView,
  onDownload
}) {
  const inputRef = useRef(null);
  const saved = Array.isArray(attachments) ? attachments : [];
  const pending = Array.isArray(pendingFiles) ? pendingFiles : [];

  const handleView = (att) => {
    if (onView) onView(att);
    else viewApplicationAttachment(att);
  };

  const handleDownload = (att) => {
    if (onDownload) onDownload(att);
    else downloadApplicationAttachment(att);
  };

  return (
    <Box sx={{ minWidth: 140, maxWidth: 220 }}>
      <Stack spacing={0.5}>
        {saved.map((att) => (
          <Stack
            key={att._id || att.fileUrl}
            direction="row"
            spacing={0.25}
            alignItems="center"
          >
            <Typography
              variant="caption"
              noWrap
              sx={{ flex: 1, minWidth: 0 }}
              title={att.fileName || 'Document'}
            >
              {att.fileName || 'Document'}
            </Typography>
            {att.fileUrl && (
              <>
                <Tooltip title="View">
                  <IconButton size="small" onClick={() => handleView(att)}>
                    <Visibility fontSize="inherit" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Download">
                  <IconButton size="small" onClick={() => handleDownload(att)}>
                    <Download fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {!readOnly && onDelete && (
              <Tooltip title="Delete">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={saving}
                    onClick={() => onDelete(att)}
                  >
                    <DeleteOutline fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        ))}

        {pending.map((file, index) => (
          <Stack
            key={`pending-${index}-${file.name}`}
            direction="row"
            spacing={0.25}
            alignItems="center"
          >
            <Typography
              variant="caption"
              noWrap
              sx={{ flex: 1, minWidth: 0 }}
              title={file.name}
            >
              {file.name}
            </Typography>
            <Tooltip title="Download">
              <IconButton size="small" onClick={() => downloadPendingFile(file)}>
                <Download fontSize="inherit" />
              </IconButton>
            </Tooltip>
            {!readOnly && onRemovePending && (
              <Tooltip title="Delete">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={saving}
                    onClick={() => onRemovePending(index)}
                  >
                    <DeleteOutline fontSize="inherit" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        ))}

        {!readOnly && onAddFiles && (
          <>
            <input
              ref={inputRef}
              type="file"
              hidden
              multiple
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) onAddFiles(files);
                e.target.value = '';
              }}
            />
            <Tooltip title="Upload attachment">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={saving}
                  onClick={() => inputRef.current?.click()}
                >
                  <AttachFile fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}

        {saved.length === 0 && pending.length === 0 && readOnly && (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default RowAttachmentsCell;
